import {
  type InsuredCoverage,
  ProductSubType,
  ProductType,
  type ShurContract
} from 'shur-api'

import { t } from '@/i18n'
import { isValidShurContract } from '@/utils/shur/SHURUtils'

/**
 * Translates an InsuredCoverage/ShurContract to the respective Title/Icon needed for the Tile/Screen
 */
export function shurInfosToScreen(
  obj: InsuredCoverage | ShurContract | null,
  onlyMainLine?: boolean
): ScreenBinding {
  if (!obj) return {} as ScreenBinding
  const subType = obj.productSubType as ProductSubType
  const type = obj.productType as ProductType

  //Prio 1: Based on ProductSubType
  if (subType && subType != 'UNKNOWN' && !onlyMainLine)
    return new ScreenBinding(obj, ProductSubTypeScreenBindings[subType])
  //Prio 2: Based on ProductType (Fallback)
  else if (type) return new ScreenBinding(obj, ProductTypeScreenBindings[type])
  //Prio 3: We have no idea what to do
  else {
    return new ScreenBinding(obj, ProductTypeScreenBindings['UNKNOWN'])
  }
}

export interface TitleAndIcon {
  firstTitle: string
  icon: string
}

export class ScreenBinding implements TitleAndIcon {
  constructor(
    protected obj: InsuredCoverage | ShurContract,
    protected titleAndIcon: TitleAndIcon
  ) {
    // nope
  }

  public get firstTitle(): string {
    if (this.obj && (this.obj as ShurContract).contractBundle) {
      return t('bundleFallbackTitle').toString()
    }

    return this.titleAndIcon?.firstTitle ?? 'Unbekannt'
  }

  public get icon(): string {
    return this.titleAndIcon?.icon ?? 'icon-package'
  }

  public get secondTitle(): string {
    if (this.obj) {
      switch (this.obj.productName) {
        // Bundel Contracts
        case 'PrivatSchutz':
          return 'PrivatSchutz'
        case 'MultiPlus':
          return 'MultiPlus'
        case 'MultiPlusmaximo':
          return 'MultiPlus<i>maximo</i>'
      }
    }
    return ''
  }
}

/**
 * Primary definitions for Title & Icon based on ProductSubType
 */
export const ProductSubTypeScreenBindings: Record<ProductSubType, TitleAndIcon> = {
  ACCIDENT: {
    firstTitle: 'Unfallversicherung',
    icon: 'Accident'
  },
  LIABILITY_PRIVATE: {
    firstTitle: 'Privat-Haftpflichtversicherung',
    icon: 'icon-phv'
  },
  LIABILITY_PET: {
    firstTitle: 'Tierhalter-Haftpflichtversicherung',
    icon: 'icon-tierhaftpflicht'
  },
  LIABILITY_SPORTS_BOAT: {
    firstTitle: 'Sportboot-Haftpflichtversicherung',
    icon: 'icon-boot'
  },
  LIABILITY_PROPERTY_OWNERS: {
    firstTitle: 'Haus- und Grundbesitzer-Haftpflichtversicherung',
    icon: 'icon-haus-und-grund'
  },
  LIABILITY_WATER_POLLUTION: {
    firstTitle: 'Gewässerschaden-Haftpflichtversicherung',
    icon: 'icon-gewaesser'
  },
  LIABILITY_HUNTING: {
    firstTitle: 'Jagd-Haftpflichtversicherung',
    icon: 'icon-jagd'
  },
  LIABILITY_BUILDERS: {
    firstTitle: 'Bauherren-Haftpflichtversicherung',
    icon: 'icon-bauherren'
  },
  HOUSEHOLDCONTENTS: {
    firstTitle: 'Hausratversicherung',
    icon: 'icon-glas_kaputt_outline'
  },
  GLAS: {
    firstTitle: 'Glasversicherung',
    icon: 'icon-glasversicherung'
  },
  EXISTENCE_PROTECTION: {
    firstTitle: 'Existenzversicherung',
    icon: 'icon-existenz'
  },
  BUILDING: {
    firstTitle: 'Wohngebäudeversicherung',
    icon: 'icon-wohnen'
  },
  LEGAL_PRIVATE_AND_PROFESSIONAL: {
    firstTitle: 'Privat- und Berufs-Rechtsschutzversicherung',
    icon: 'icon-rechtsschutz'
  },
  LEGAL_ROAD_USERS: {
    firstTitle: 'Verkehrs-Rechtsschutzversicherung',
    icon: 'icon-verkehr'
  },
  LEGAL_PROPERTY: {
    firstTitle: 'Immobilien-Rechtsschutzversicherung',
    icon: 'icon-immo-rechtsschutz'
  },
  LEGAL_PROPERTY_LANDLORD: {
    firstTitle: 'Immobilien-Rechtsschutzversicherung',
    icon: 'icon-immo-rechtsschutz'
  },
  LEGAL_PROPERTY_OWNER: {
    firstTitle: 'Immobilien-Rechtsschutzversicherung',
    icon: 'icon-immo-rechtsschutz'
  },
  LEGAL_PROPERTY_TENANT: {
    firstTitle: 'Immobilien-Rechtsschutzversicherung',
    icon: 'icon-immo-rechtsschutz'
  },
  LEGAL_DRIVER: {
    firstTitle: 'Fahrer-Rechtsschutzversicherung',
    icon: 'icon-verkehr'
  },
  LEGAL_PRIVATE_PROFESSIONAL_FAMILY_TRAFFIC: {
    firstTitle: 'Privat-, Berufs- und Familien-Verkehrs- Rechtsschutzversicherung',
    icon: 'icon-verkehr'
  },
  LEGAL_FAMILY_TRAFFIC: {
    firstTitle: 'Familien-Verkehrs-Rechtsschutzversicherung',
    icon: 'icon-verkehr'
  },
  LEGAL_EXTENDED_CRIMINAL: {
    firstTitle: 'Erweiterter Straf-Rechtsschutz',
    icon: 'icon-rechtsschutz'
  },
  TRAVEL_COST: {
    firstTitle: 'Reiserücktrittskosten-Versicherung',
    icon: 'icon-reise'
  },
  TRAVEL_BAGGAGE: {
    firstTitle: 'Reisegepäckversicherung',
    icon: 'icon-reise'
  },
  TRAVEL_HEALTH: {
    firstTitle: 'Auslandsreisekrankenversicherung',
    icon: 'icon-ausland-kv'
  },
  WEDDING: {
    firstTitle: 'Hochzeitsversicherung',
    icon: 'icon-package'
  },
  VAULT_CONTENTS: {
    firstTitle: 'Tresorinhaltsversicherung',
    icon: 'icon-safe'
  },
  UNKNOWN: {
    firstTitle: 'Unbekannt',
    icon: 'icon-package'
  }
}

/**
 * (Fallback) Secondary Definitions based on ProductType (GDV Sparte) in case ProductSubType is UNKNOWN
 */
export const ProductTypeScreenBindings: Record<ProductType, TitleAndIcon> = {
  ACCIDENT: {
    firstTitle: 'Unfallversicherung',
    icon: 'Accident'
  },
  BUILDING: {
    firstTitle: 'Wohngebäudeversicherung',
    icon: 'icon-wohnen'
  },
  GLAS: {
    firstTitle: 'Glasversicherung',
    icon: 'icon-glas_kaputt_outline'
  },
  HOUSEHOLDCONTENTS: {
    firstTitle: 'Hausratversicherung',
    icon: 'icon-glas_kaputt_outline'
  },
  LEGALPROTECTION: {
    firstTitle: 'Rechtsschutzversicherung',
    icon: 'icon-rechtsschutz'
  },
  LIABILITY: {
    firstTitle: 'Haftpflichtversicherung',
    icon: 'icon-phv'
  },
  TRANSPORT: {
    firstTitle: 'Sonstige Absicherung',
    icon: 'icon-weitere_VS'
  },
  TRAVEL_HEALTH: {
    firstTitle: 'Auslandsreisekrankenversicherung',
    icon: 'icon-ausland-kv'
  },
  BUNDLE: {
    firstTitle: '',
    icon: 'icon-package'
  },
  UNKNOWN: {
    firstTitle: 'Unbekannt',
    icon: 'icon-package'
  }
}

export function shurIconStringResolved(shurContract: ShurContract | null) {
  if (!isValidShurContract(shurContract)) {
    return 'icon-package'
  }
  return shurInfosToScreen(shurContract).icon
}

export function shurFirstTitle(shurContract: ShurContract | null) {
  if (!isValidShurContract(shurContract)) {
    return t('bundleFallbackTitle').toString()
  }
  return shurInfosToScreen(shurContract).firstTitle
}

export function shurSecondTitle(shurContract: ShurContract | null) {
  if (!isValidShurContract(shurContract)) {
    return ''
  }
  return shurInfosToScreen(shurContract).secondTitle
}
